import { useState, useEffect, useCallback } from 'react';
import { 
  ProjectPlanningCategory, 
  ProjectTypeDefinition, 
  ProjectTypeWizardState,
  ProjectTypeRecommendation as ProjectTypeRec
} from '../types/project-types';
import { 
  ProjectTypeConfig, 
  ProjectTypeRecommendation,
  ProjectPlanningStep,
  ProjectArtifact,
  Project
} from '../types';
import { getProjectTypeDefinition, recommendProjectType, getAllProjectTypes } from '../config/project-type-definitions';
import { apiService as api } from '../services/api';

export const useProjectTypes = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get all available project type definitions
  const getProjectTypeDefinitions = useCallback((): ProjectTypeDefinition[] => {
    return getAllProjectTypes();
  }, []);

  // Get specific project type definition
  const getDefinition = useCallback((category: ProjectPlanningCategory): ProjectTypeDefinition => {
    return getProjectTypeDefinition(category);
  }, []);

  // Recommend project type based on description
  const getRecommendation = useCallback((description: string): ProjectTypeRec[] => {
    return recommendProjectType(description);
  }, []);

  // Create project type configuration
  const createProjectTypeConfig = useCallback(async (
    projectId: string, 
    config: Partial<ProjectTypeConfig>
  ): Promise<ProjectTypeConfig | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post(`/projects/${projectId}/type-config`, config);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create project type configuration');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update project type configuration
  const updateProjectTypeConfig = useCallback(async (
    projectId: string,
    config: Partial<ProjectTypeConfig>
  ): Promise<ProjectTypeConfig | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.put(`/projects/${projectId}/type-config`, config);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update project type configuration');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get project type configuration
  const getProjectTypeConfig = useCallback(async (projectId: string): Promise<ProjectTypeConfig | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/projects/${projectId}/type-config`);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to get project type configuration');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create planning step
  const createPlanningStep = useCallback(async (
    projectId: string,
    step: Partial<ProjectPlanningStep>
  ): Promise<ProjectPlanningStep | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post(`/projects/${projectId}/planning-steps`, step);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create planning step');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update planning step
  const updatePlanningStep = useCallback(async (
    projectId: string,
    stepId: string,
    updates: Partial<ProjectPlanningStep>
  ): Promise<ProjectPlanningStep | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.put(`/projects/${projectId}/planning-steps/${stepId}`, updates);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update planning step');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get planning steps for project
  const getPlanningSteps = useCallback(async (projectId: string): Promise<ProjectPlanningStep[]> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/projects/${projectId}/planning-steps`);
      return response.data || [];
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to get planning steps');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Complete planning step
  const completePlanningStep = useCallback(async (
    projectId: string,
    stepId: string,
    data?: Record<string, any>,
    notes?: string
  ): Promise<ProjectPlanningStep | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post(`/projects/${projectId}/planning-steps/${stepId}/complete`, {
        stepData: data,
        notes
      });
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete planning step');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create project artifact
  const createArtifact = useCallback(async (
    projectId: string,
    artifact: Partial<ProjectArtifact>
  ): Promise<ProjectArtifact | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post(`/projects/${projectId}/artifacts`, artifact);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create artifact');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get project artifacts
  const getArtifacts = useCallback(async (projectId: string): Promise<ProjectArtifact[]> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/projects/${projectId}/artifacts`);
      return response.data || [];
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to get artifacts');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Save project type recommendation
  const saveRecommendation = useCallback(async (
    projectId: string,
    recommendation: Partial<ProjectTypeRecommendation>
  ): Promise<ProjectTypeRecommendation | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post(`/projects/${projectId}/type-recommendations`, recommendation);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save recommendation');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Accept project type recommendation
  const acceptRecommendation = useCallback(async (
    projectId: string,
    recommendationId: string
  ): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      await api.post(`/projects/${projectId}/type-recommendations/${recommendationId}/accept`);
      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to accept recommendation');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Validate project data against type rules
  const validateProject = useCallback((
    project: Partial<Project>,
    definition: ProjectTypeDefinition
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Validate required fields
    definition.requiredFields.forEach(field => {
      if (field.required) {
        const value = project.planningMetadata?.steps?.find(s => s.stepData[field.key]) 
          || (project as any)[field.key];
        
        if (!value) {
          errors.push(`${field.label} ist erforderlich`);
        }
      }
    });

    // Validate field constraints
    definition.validationRules.forEach(rule => {
      const value = (project as any)[rule.field];
      
      switch (rule.rule) {
        case 'required':
          if (!value) errors.push(rule.errorMessage);
          break;
        case 'date_future':
          if (value && new Date(value) <= new Date()) {
            errors.push(rule.errorMessage);
          }
          break;
        case 'min':
          if (value !== undefined && value < rule.value) {
            errors.push(rule.errorMessage);
          }
          break;
        case 'max':
          if (value !== undefined && value > rule.value) {
            errors.push(rule.errorMessage);
          }
          break;
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }, []);

  return {
    loading,
    error,
    // Definitions
    getProjectTypeDefinitions,
    getDefinition,
    getRecommendation,
    // Configuration
    createProjectTypeConfig,
    updateProjectTypeConfig,
    getProjectTypeConfig,
    // Planning Steps
    createPlanningStep,
    updatePlanningStep,
    getPlanningSteps,
    completePlanningStep,
    // Artifacts
    createArtifact,
    getArtifacts,
    // Recommendations
    saveRecommendation,
    acceptRecommendation,
    // Validation
    validateProject
  };
};

// Wizard-specific hook
export const useProjectTypeWizard = (projectId?: string) => {
  const [wizardState, setWizardState] = useState<ProjectTypeWizardState>({
    currentStep: 0,
    totalSteps: 3,
    formData: {},
    completedSteps: [],
    validationErrors: {}
  });

  const updateWizardState = useCallback((updates: Partial<ProjectTypeWizardState>) => {
    setWizardState(prev => ({ ...prev, ...updates }));
  }, []);

  const goToNextStep = useCallback(() => {
    setWizardState(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, prev.totalSteps - 1)
    }));
  }, []);

  const goToPreviousStep = useCallback(() => {
    setWizardState(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 0)
    }));
  }, []);

  const completeStep = useCallback((stepName: string) => {
    setWizardState(prev => ({
      ...prev,
      completedSteps: [...prev.completedSteps.filter(s => s !== stepName), stepName]
    }));
  }, []);

  const setValidationError = useCallback((field: string, error: string) => {
    setWizardState(prev => ({
      ...prev,
      validationErrors: { ...prev.validationErrors, [field]: error }
    }));
  }, []);

  const clearValidationError = useCallback((field: string) => {
    setWizardState(prev => ({
      ...prev,
      validationErrors: { ...prev.validationErrors, [field]: undefined }
    }));
  }, []);

  const resetWizard = useCallback(() => {
    setWizardState({
      currentStep: 0,
      totalSteps: 3,
      formData: {},
      completedSteps: [],
      validationErrors: {}
    });
  }, []);

  return {
    wizardState,
    updateWizardState,
    goToNextStep,
    goToPreviousStep,
    completeStep,
    setValidationError,
    clearValidationError,
    resetWizard
  };
};